import Creatable from "react-select/creatable";
import React, { useState } from "react";
import qapi from "views_shared/app/javascript/api";
import { withAsyncPaginate } from "react-select-async-paginate";

const CreatableAsyncPaginate = withAsyncPaginate(Creatable);

// qapi.mediaOutlets.getAll already returns react-select-shaped options
// ({ label, value, domain, prestige, imageUrl }) and throws on error, so there's no data/wrapper to
// unpack (unlike the PR CompanyPicker's qapi.companies.getAll). It also has no pagination cursor — it
// takes a `limit` but returns no remaining-rows count — so we load a single page and report hasMore:false.
const loadOptions = async (query, _loadedOptions, { page, perPage }) => {
  try {
    const options = await qapi.mediaOutlets.getAll({ search: query, limit: perPage });

    return {
      options,
      hasMore: false,
      additional: { page: page + 1, perPage },
    };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Error loading options:", error);
    return {
      options: [],
      hasMore: false,
      additional: { page, perPage },
    };
  }
};

// Media jobs allowlist only `media_outlet_id` (Id::MediaJobPolicy) — there is NO name-based outlet
// creation through the jobs endpoint, unlike the PR side which defers company creation to the server via
// `company_name`. So when the user creates a new outlet we must create it FIRST (qapi.mediaOutlets.create,
// which returns the new outlet's id) and only then hand the resolved id back to the row. If the create
// fails we surface it locally and DO NOT call onOutletChange, so the row keeps its previous outlet rather
// than being left in a half-picked state pointing at an outlet that was never persisted.
function OutletPicker({ mediaOutletName, mediaOutletId, onOutletChange, onCreatingChange = () => {} }) {
  const [createError, setCreateError] = useState(null);
  const [creating, setCreating] = useState(false);

  // Creating an outlet takes a beat server-side: keep the flag both locally (to drive the control's own
  // spinner via isLoading) and forwarded to the row (which disables confirm). isDisabled is on too —
  // changing the selection while the create is resolving would race the create's onChange and leave the
  // row pointing at the wrong outlet.
  const setCreatingState = (value) => {
    setCreating(value);
    onCreatingChange(value);
  };

  const handleChange = (option) => {
    setCreateError(null);
    onOutletChange(option);
  };

  const onOutletCreate = async (name) => {
    setCreateError(null);
    setCreatingState(true);
    try {
      const created = await qapi.mediaOutlets.create({ name });
      onOutletChange({ label: created.name || name, value: created.id });
    } catch (error) {
      setCreateError(error.message || "Failed to create outlet");
    } finally {
      setCreatingState(false);
    }
  };

  return (
    <>
      <CreatableAsyncPaginate
        value={{ label: mediaOutletName, value: mediaOutletId }}
        loadOptions={loadOptions}
        onChange={handleChange}
        onCreateOption={onOutletCreate}
        isLoading={creating}
        isDisabled={creating}
        // The editor table sits in a horizontally-scrollable wrapper (overflow != visible), which would
        // otherwise clip this dropdown; portal the menu to the body so it escapes that scroll context.
        // menuPortal zIndex clears the Bootstrap modal; option wraps a long "Create <name>" within the
        // (now bounded) menu width instead of overflowing.
        menuPortalTarget={document.body}
        menuPosition="fixed"
        styles={{
          option: (base) => ({ ...base, overflowWrap: "anywhere" }),
          menuPortal: (base) => ({ ...base, zIndex: 1060 }),
        }}
        additional={{ page: 1, perPage: 24 }}
        debounceTimeout={300}
        defaultOptions
      />
      {createError && <span className="small text-danger">{createError}</span>}
    </>
  );
}

export default OutletPicker;
